import React from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import {
  LayoutGrid, Users, Store as StoreIcon, BadgeCheck, ShieldHalf, ShoppingBag, Package, ClipboardList,
  Star, LayoutList, Truck, Bike, Settings2, CreditCard, HandCoins, Banknote, QrCode, Send, Undo2, Handshake, Trash2, Plus,
  Percent, Crown, Megaphone, Flag, Scale, MessageSquareWarning, LifeBuoy, BarChart3, KeyRound, ScrollText, Settings, Gift,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { CenterLayout } from '../components/commerce/CenterLayout';
import { Stub } from './admin/common';
import { AdminDashboard } from './admin/Dashboard';
import { DeliveryOps, DeliverySettings } from './admin/Delivery';
import { OrdersAdmin } from './admin/Orders';
import { CashReconciliation, DigitalCollections, Payouts } from './admin/Finance';
import { Verification, MerchantsAdmin, Subscriptions } from './admin/Merchants';
import { NotificationsCenter, AuditLog, Analytics } from './admin/Insights';
import { MarketingCampaigns, PartnersAdmin, HomepageCms, MadeInKelantanCms, HomepageHeroCms } from './admin/Marketing';
import { StaffManager, SupportAdmin, ReferralsAdmin } from './admin/People';
import { WalletAdmin } from './admin/Wallet';

export default function AdminCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { unreadCount, deliveries, payouts } = useApp();
  React.useEffect(() => {
    if (user === false || (user && user.role !== 'admin')) navigate('/account/login?redirect=/admin');
  }, [user, navigate]);
  if (!user || user.role !== 'admin') return null;
  const pendingPickups = deliveries.filter((d) => d.deliveryStatus === 'WAITING_PICKUP').length;
  const payoutsReady = payouts.filter((p) => p.status === 'READY').length;

  const groups = [
    { label: 'Overview', items: [{ to: '/admin', icon: LayoutGrid, label: 'Dashboard', end: true }, { to: '/admin/notifications', icon: Megaphone, label: 'Notifications', badge: unreadCount('admin') }] },
    {
      label: 'People', items: [
        { to: '/admin/consumers', icon: Users, label: 'Consumers' },
        { to: '/admin/merchants', icon: StoreIcon, label: 'Merchants' },
        { to: '/admin/verification', icon: BadgeCheck, label: 'Verification' },
        { to: '/admin/staff', icon: ShieldHalf, label: 'Staff Accounts' },
      ],
    },
    {
      label: 'Commerce', items: [
        { to: '/admin/stores', icon: ShoppingBag, label: 'Stores' },
        { to: '/admin/products', icon: Package, label: 'Products' },
        { to: '/admin/orders', icon: ClipboardList, label: 'Orders' },
        { to: '/admin/reviews', icon: Star, label: 'Reviews' },
        { to: '/admin/categories', icon: LayoutList, label: 'Categories' },
      ],
    },
    {
      label: 'Delivery', items: [
        { to: '/admin/delivery', icon: Truck, label: 'Delivery Operations', badge: pendingPickups },
        { to: '/admin/delivery/staff', icon: Bike, label: 'Delivery Staff' },
        { to: '/admin/delivery/settings', icon: Settings2, label: 'Delivery Settings' },
      ],
    },
    {
      label: 'Finance', items: [
        { to: '/admin/finance/payments', icon: CreditCard, label: 'Payments' },
        { to: '/admin/wallet', icon: HandCoins, label: 'Wallets' },
        { to: '/admin/finance/cash', icon: Banknote, label: 'Cash' },
        { to: '/admin/finance/digital-collections', icon: QrCode, label: 'Digital Collections' },
        { to: '/admin/finance/payouts', icon: Send, label: 'Payouts', badge: payoutsReady },
        { to: '/admin/finance/refunds', icon: Undo2, label: 'Refunds' },
        { to: '/admin/finance/commission', icon: Percent, label: 'Commission' },
        { to: '/admin/subscriptions', icon: Crown, label: 'Subscriptions' },
      ],
    },
    {
      label: 'Growth', items: [
        { to: '/admin/partners', icon: Handshake, label: 'Partners' },
        { to: '/admin/marketing', icon: Megaphone, label: 'Marketing' },
        { to: '/admin/made-in-kelantan', icon: Flag, label: 'Made in Kelantan' },
        { to: '/admin/homepage-hero', icon: LayoutList, label: 'Homepage Hero' },
        { to: '/admin/homepage', icon: LayoutList, label: 'Homepage' },
      ],
    },
    {
      label: 'Trust', items: [
        { to: '/admin/reports', icon: Flag, label: 'Reports' },
        { to: '/admin/disputes', icon: Scale, label: 'Disputes' },
        { to: '/admin/moderation', icon: MessageSquareWarning, label: 'Moderation' },
        { to: '/admin/support', icon: LifeBuoy, label: 'Support' },
        { to: '/admin/referrals', icon: Gift, label: 'Referrals' },
      ],
    },
    { label: 'Insights', items: [{ to: '/admin/analytics', icon: BarChart3, label: 'Analytics' }] },
    {
      label: 'System', items: [
        { to: '/admin/roles', icon: KeyRound, label: 'Roles & Permissions' },
        { to: '/admin/audit-log', icon: ScrollText, label: 'Audit Log' },
        { to: '/admin/settings', icon: Settings, label: 'Settings' },
      ],
    },
  ];

  return (
    <CenterLayout title="Admin Center" groups={groups} recipientType="admin" fullNotifUrl="/admin/notifications">
      <Routes>
        <Route index element={<AdminDashboard />} />
        <Route path="notifications" element={<NotificationsCenter />} />
        <Route path="merchants" element={<MerchantsAdmin />} />
        <Route path="verification" element={<Verification />} />
        <Route path="delivery" element={<DeliveryOps />} />
        <Route path="delivery/settings" element={<DeliverySettings />} />
        <Route path="finance/cash" element={<CashReconciliation />} />
        <Route path="finance/digital-collections" element={<DigitalCollections />} />
        <Route path="finance/payouts" element={<Payouts />} />
        <Route path="subscriptions" element={<Subscriptions />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="audit-log" element={<AuditLog />} />
        <Route path="consumers" element={<Stub title="Consumers" />} />
        <Route path="staff" element={<StaffManager />} />
        <Route path="stores" element={<Stub title="Stores" />} />
        <Route path="products" element={<Stub title="Products" />} />
        <Route path="orders" element={<OrdersAdmin />} />
        <Route path="reviews" element={<Stub title="Reviews" />} />
        <Route path="categories" element={<Stub title="Categories" />} />
        <Route path="delivery/staff" element={<Stub title="Delivery Staff">Rider accounts are created and managed under Staff.</Stub>} />
        <Route path="finance/payments" element={<Stub title="Payments">All pay-on-delivery transactions flow through Cash and Digital Collections.</Stub>} />
        <Route path="wallet" element={<WalletAdmin />} />
        <Route path="finance/refunds" element={<Stub title="Refunds" />} />
        <Route path="finance/commission" element={<Stub title="Commission">STORE 5% · BOOST 4% · BRAND 3% — calculated on item subtotal after merchant-funded discounts. Delivery fees are never commissioned.</Stub>} />
        <Route path="partners" element={<PartnersAdmin />} />
        <Route path="marketing" element={<MarketingCampaigns />} />
        <Route path="homepage" element={<HomepageCms />} />
        <Route path="made-in-kelantan" element={<MadeInKelantanCms />} />
        <Route path="homepage-hero" element={<HomepageHeroCms />} />
        <Route path="reports" element={<Stub title="Reports" />} />
        <Route path="disputes" element={<Stub title="Disputes" />} />
        <Route path="moderation" element={<Stub title="Moderation" />} />
        <Route path="support" element={<SupportAdmin />} />
        <Route path="referrals" element={<ReferralsAdmin />} />
        <Route path="roles" element={<Stub title="Roles & Permissions" />} />
        <Route path="settings" element={<Stub title="System Settings" />} />
      </Routes>
    </CenterLayout>
  );
}
